/**
 * English. Also the fallback: a key missing here shows as the key itself, which is ugly on
 * purpose — a blank where a sentence belongs looks like the product working.
 */
export const en: Record<string, string> = {
  // ---------------------------------------------------------------- shell
  'app.name': "Norva",
  'app.definitions': 'Definitions',
  'app.signOut': 'Sign out',
  'app.updateReady': 'A new version is ready.',
  'app.updateNow': 'Reload now',
  'app.language': 'Language',

  'app.templates': 'Templates',
  'nav.templates': 'Form Templates',

  'templates.title': 'Form Templates',
  'templates.subtitle': '{count} templates · where the three definitions come together',
  'templates.code': 'Code',
  'templates.codeHint': 'Fixed across versions. It is how an audit says which form it was measured against.',
  'templates.name': 'Name',
  'templates.visitType': 'Visit Type',
  'templates.notice': 'Announced / Unannounced',
  'templates.scoring': 'Scoring',
  'templates.scored': 'Scored',
  'templates.unscored': 'Not scored',
  'templates.status': 'Status',
  'templates.period': 'Valid',
  'templates.from': 'Valid from',
  'templates.to': 'Valid to',
  'templates.questions': 'Questions',
  'templates.open': 'Open',
  'templates.empty': 'No templates yet.',
  'templates.notFound': 'No such template.',
  'templates.backToList': 'Back to the list',
  'visitTypes.templateChip': 'Template',
  'templates.publishTitle': 'Publish {name} v{version}?',
  'templates.publishBody': 'A published version keeps its questions, weights and points for good; a change means a new version. Audits started on it are scored against it.',
  'templates.outOfPeriod': 'out of period',
  'templates.superseded': 'replaced by v{version}',
  'templates.supersededHint': 'Audits started on this version finish on it; new audits and plans open the newest version.',
  'templates.summary': '{questions} questions · {categories} categories',
  'templates.weight': 'Weight',
  'templates.share': 'Share',
  'templates.moveUp': 'Move up',
  'templates.moveDown': 'Move down',
  'templates.removeQuestion': 'Remove from form',
  'templates.removeCategory': 'Remove category',
  'templates.approvalChain': 'Action approval chain',
  'templates.approvalChainHint': 'Approved actions from this checklist travel these seats in order. Left empty, an action closes on the owner\'s word. A step names a kind of seat: pick one that covers a region and an action goes to the seat of that kind over the store it came from — so one checklist works across every region. Seats that cover everywhere, such as a director or a quality manager, are taken as they are.',
  'templates.addApprover': 'Add an approver…',
  'templates.chainOrder': 'In order',
  'templates.chainOrderHint': 'After the owner answers, it goes to these seats in turn. The last one closes it.',
  'templates.removeApprover': 'Remove approver',
  'templates.noChain': 'No chain: actions from this checklist close in one step, on the owner\'s word.',
  'error.form.approval-step.position.required': 'An approval step needs a seat.',
  'templates.removeCategoryTitle': 'Remove the category {name}?',
  'templates.removeCategoryWarning': 'Its {count} questions go with it. They can be picked out of the library again; what they were weighted at cannot.',
  'templates.noQuestions': 'No questions yet. Pick some from the panel on the right.',
  'templates.addQuestions': 'Add questions',
  'templates.alreadyOnForm': 'on the form',
  'templates.picked': '{count} selected',
  'templates.pickSome': 'Select questions',
  'templates.addSelected': 'Add selected',
  'templates.publish': 'Publish',
  'templates.archive': 'Archive',
  'templates.newVersion': 'New version (v{version})',
  'templates.goToNewest': 'This template has a v{version} — go there',
  'templates.flatten': 'Match category weights to their questions',
  'templates.points': 'Points',
  'templates.totalPoints': '{total} of {target} points',
  'templates.pointsHint':
    'Every question carries its own points; a category is worth the sum of them and cannot be set by hand. The form cannot be published until the total hits the target. A question ruled out leaves the form whatever its library setting, and the rest are scaled to the target.',
  'templates.scoringModel': 'Scoring',
  'templates.scoringModelHint':
    'Weight: the numbers are relative. Points: every question carries its own points and the total must hit the target. Frozen once published.',
  'templates.flattenHint': "Matching sets each category's weight to the sum of its questions, so a category with more questions carries more. The two give different numbers for the same answers.",
  'templates.cannotPublish': 'Cannot publish:',
  'templates.readyToPublish': 'Ready to publish.',
  'templates.lockedNotice': 'This template is published. Its questions and weights are fixed — past audits were scored against v{version}.',
  'templates.totalWeight': 'total weight {weight}',

  'formStatus.Draft': 'Draft',
  'formStatus.Published': 'Published',
  'formStatus.Archived': 'Archived',
  'notice.Announced': 'Announced',
  'notice.Unannounced': 'Unannounced',

  'error.form.period.backwards': 'The end of the period comes before its start, which makes a template nobody can use.',
  'error.form.not-draft': 'A published template cannot be changed. Create a new version instead.',
  'error.location.out-of-reach': '{code} is not in your part of the organisation. Ask your administrator if you think that is wrong.',
  'error.form.not-latest': 'Version {version} of this template already exists. A new version is made from the newest one.',
  'error.form.weight.positive': 'A weight must be greater than zero.',
  'error.form.points-target.positive': 'The points target must be greater than zero.',
  'error.form.category.weight-derived':
    'Under points a category is worth the sum of its questions. Change a question instead.',

  'nav.visitTypes': 'Visit Types',
  'nav.categories': 'Categories',
  'nav.questions': 'Questions',

  // ---------------------------------------------------------------- common
  'common.add': "Add",
  'common.save': 'Save',
  'common.cancel': "Cancel",
  'common.dismiss': "Never mind",
  'common.edit': 'Edit',
  'common.delete': 'Delete',
  'common.close': 'Close',
  'common.previous': 'Previous',
  'common.retry': 'Try again',
  'common.next': 'Next',
  'common.loading': 'Loading…',
  'common.optional': 'Optional',
  'common.none': '—',
  'common.retire': 'Retire',
  'common.reinstate': 'Reinstate',

  // ---------------------------------------------------------------- sign in
  'login.title': 'Sign in',
  'login.email': 'Email',
  'login.password': 'Password',
  'login.submit': 'Sign in',
  'login.working': 'Checking…',
  'login.failed': 'Email or password is wrong.',

  // ---------------------------------------------------------------- visit types
  'visitTypes.title': 'Visit Types',
  'visitTypes.subtitle': 'What an audit is about. Chosen when a template is created.',
  'visitTypes.name': 'Name',
  'visitTypes.namePlaceholder': 'Visit type name',
  'visitTypes.description': 'Description',
  'visitTypes.templates': 'Templates',
  'visitTypes.empty': 'No visit types yet.',
  'visitTypes.inUse': '{count} templates',
  'visitTypes.unused': 'unused',

  // ---------------------------------------------------------------- categories
  'categories.title': 'Categories',
  'categories.subtitle':
    'Question headings. The code is the prefix of every question code under it — Visual Operations → VIS-01.',
  'categories.name': 'Category',
  'categories.namePlaceholder': 'Category name',
  'categories.code': 'Code',
  'categories.description': 'Description',
  'categories.questions': 'Questions',
  'categories.questionCount': '{count} questions',
  'categories.empty2': 'empty',
  'categories.empty': 'No categories yet.',
  'categories.inUseTitle': 'In use, cannot be deleted',

  // ---------------------------------------------------------------- questions
  'questions.title': 'Questions',
  'questions.subtitle': '{count} questions · templates are built from these',
  'questions.search': 'Search text or code…',
  'questions.allCategories': 'All categories',
  'questions.category': 'Category',
  'questions.code': 'Code',
  'questions.text': 'Question',
  'questions.type': 'Type',
  'questions.behaviour': 'Behaviour',
  'questions.usage': 'Usage',
  'questions.usedIn': '{count} templates',
  'questions.unused': 'unused',
  'questions.empty': 'No questions match this filter.',
  'questions.needCategory': 'Define a category first — a question code comes from its category prefix.',
  'questions.retired': 'retired',
  'questions.inUseTitle': 'Used by templates',

  'questions.editTitle': 'Edit Question',
  'questions.newTitle': 'New Question',
  'questions.codeHint': 'Suggested from the category. You can change it.',
  'questions.textPlaceholder': 'The sentence the auditor reads on screen',
  'questions.guidance': 'Guidance',
  'questions.guidancePlaceholder': "What to look at. The auditor opens it with 'Show guidance'.",
  'questions.scaleMaximum': 'Maximum value',
  'questions.scaleHint': 'The auditor picks between 1 and {max}. Score = weight × chosen ÷ {max}.',
  'questions.scoring': 'Scoring behaviour',
  'questions.scoringHint': "The scoring engine's per-question rules.",
  'questions.critical': 'Critical item',
  'questions.commentRequirement': 'Comment required',
  'questions.photoRequirement': 'Photo required',
  'questions.allowNotApplicable': 'The auditor may mark this not applicable',
  'questions.notApplicableRule': 'If marked not applicable, its points go to the other questions of the category in proportion to theirs; the category keeps its worth. If every question in a category is not applicable, the category drops out and its points go to the other categories in proportion — the total is always 100.',

  'chip.criticalForm': 'critical · audit',
  'chip.criticalCategory': 'critical · category',
  'chip.comment': 'comment',
  'chip.photo': 'photo',
  'chip.notApplicableForm': 'N/A · form',
  'chip.notApplicableCategory': 'N/A · category',

  // ---------------------------------------------------------------- enumerations
  'answerType.YesNo': 'Yes / No',
  'answerType.Scale': 'Slider',
  'critical.None': 'None',
  'critical.Category': 'Zeroes its category',
  'critical.Form': 'Zeroes the audit',
  'trigger.Never': 'Never',
  'trigger.WhenNegative': 'On a negative answer',
  'trigger.Always': 'On every answer',
  'naScope.Category': 'Spreads within the category',
  'naScope.Form': 'Leaves the form',

  // ---------------------------------------------------------------- errors
  'error.category.name.required': 'A category needs a name.',
  'error.category.code.length':
    "'{code}' will not do as a prefix: between two and six letters. It is typed on paper checklists and read out in stores.",
  'error.category.code.letters-only':
    "'{code}' must be letters only — a question code is split on its hyphen, and digits in the prefix make that ambiguous.",

  'error.question.code.required': 'A question needs a code. It is what the auditor reads out in the store.',
  'error.question.text.required': 'A question needs its text.',
  'error.question.category.required': 'A question belongs to a category.',
  'error.question.critical.text': "'{question}' is free text: there is no answer to fail, so it cannot be critical.",
  'error.question.options.duplicate': "'{option}' is listed twice. Options must differ from one another.",
  'error.code.too-long': 'A code is at most {max} characters.',
  'error.closed-day.date.required': 'Choose a valid date.',
  'error.question.critical-and-skippable':
    "'{question}' can be marked not applicable, so it cannot also be critical. Turn off not-applicable first, or leave the question uncritical.",
  'error.question.skippable-and-critical':
    "'{question}' is a critical check and cannot be made skippable. Clear the critical setting first.",
  'error.question.scale.not-a-scale': 'A scale maximum only means something on a scale question.',
  'error.question.scale.too-small': 'A scale needs at least 2 points; one point is a yes/no question with extra steps.',

  'error.visit-type.name.required': 'A visit type needs a name.',

  'error.library.category.in-use':
    "'{name}' holds {count} questions. Deleting it would put them beyond reach; move them to another category first.",
  'error.library.question.in-use':
    "'{code}' is used by {count} templates. Retire it instead of deleting it, so the trail back to what was asked survives.",
  'error.library.visit-type.in-use':
    "'{name}' is named by {count} templates. Retire it instead of deleting it, so those templates and the audits scored against them keep their meaning.",
  'error.common.duplicate-code': 'That code or name is already taken. Codes are unique within a tenant.',

  'problem.audit.signature-required': 'This visit has to be signed for.',
  'problem.audit.comment-required': '“{question}” needs a comment.',
  'problem.audit.photo-required': '“{question}” needs a photo.',
  'problem.audit.unanswered': '“{question}” has not been answered.',
  'problem.audit.answer.scale-for-yes-no': '“{question}” is a yes/no question but was answered with a scale value.',
  'problem.audit.answer.yes-no-for-scale': '“{question}” is a scale question but was answered yes/no.',
  'problem.audit.answer.out-of-scale': '“{question}” was answered {value}, which is outside its scale of {min} to {max}.',
  'problem.audit.answer.too-many-options': '“{question}” takes one answer, and {count} were given.',
  'problem.audit.answer.not-text': '“{question}” is a free-text question and was answered some other way.',
  'problem.audit.answer.unknown-options': '“{question}” was answered with {count} option(s) it does not offer.',
  'problem.audit.answer.off-form': 'There is an answer for a question that is not on the form ({question}) — form {form}.',
  'problem.audit.not-applicable-refused': '“{question}” may not be marked not applicable on this form.',
  'problem.audit.critical-skipped': '“{question}” is a critical check and cannot be skipped.',
  'problem.form.no-categories': 'The form has no categories.',
  'problem.form.category.empty': 'The category “{category}” has no questions.',
  'problem.form.category.no-weight': 'The category “{category}” has no question weight to distribute.',
  'problem.form.no-category-weight': 'A scored form needs at least some category weight.',
  'problem.form.points.total': 'The question points come to {total}; they must come to {target}.',
  'problem.form.duplicate-question-code': 'The question code “{code}” is used by {count} questions.',
  'problem.question.too-few-options': '“{question}” is a choice question with {count} option(s). A choice between fewer than two things is not a choice.',
  'problem.question.options-worth-nothing': 'Every option on “{question}” is worth nothing, so it can never score anything.',
  'problem.question.text-cannot-be-critical': '“{question}” is free text and cannot be critical: there is no answer to judge, so there is nothing that could fail.',
  'problem.question.critical-and-optional': '“{question}” is critical but may be marked not applicable. A critical check cannot be optional.',
  'problem.score-bands.leave-a-gap': 'No band covers scores below {lowest}.',
  'error.file.key.shape': 'That file key could not be read. Something has gone wrong; ask your administrator.',
  'error.file.kind.unknown': 'A file of that kind cannot be stored.',
  'error.file.record.required': 'A file has to belong to a record.',
  'error.library.category.not-found': 'No such category.',

  'error.session.expired': 'Your session has ended.',
  'error.unexpected': 'Something went wrong.',
  'error.plan.location.required': 'A plan is for a store.',
  'error.plan.form.required': 'A plan is for a checklist.',
  'error.plan.assignee.required': 'A plan is somebody’s work; choose who.',
  'error.plan.audit.required': 'Starting a plan creates an audit.',
  'error.plan.period.backwards': 'A period ends after it starts.',
  'error.plan.closed': 'This plan is closed and cannot be changed.',
  'error.plan.already-started': 'There is already an open plan for this store in this period.',
  'error.plan.not-started': 'A plan is completed by the audit it started.',
  'error.plan.cancel.needs-reason': 'Say why the visit is not happening.',
  'error.plan.assignee.out-of-reach': 'That person cannot reach this store, so the task would never appear for them.',
  'error.plan.assignee.cannot-audit': 'That person has no permission to conduct an audit, so this visit could never be made.',
  'error.plan.period.all-closed': 'Every day of the period is one the store is shut.',
  'error.plan.started.fixed': 'The visit is under way: its person and start stay, only the end can move later.',
  'error.form.not-published': 'The checklist is not published. A plan cannot point at a draft.',
  'error.listFailed': 'Could not load the list.',
  'error.saveFailed': 'Could not save.',
  'error.deleteFailed': 'Could not delete.',
  'error.actionFailed': 'Could not complete that.',

  'validation.criticalAndSkippable':
    'A critical item cannot be marked not applicable — a critical check the auditor can skip is not a critical check.',

  // ---------------------------------------------------------------- organisation
  'app.organisation': 'Organisation',
  'app.geography': 'Geography',
  'nav.locationTypes': 'Location types',
  'nav.departments': 'Departments',
  'nav.systemParameters': 'System Parameters',

  'systemParameters.title': 'System Parameters',
  'systemParameters.subtitle': 'The settings that decide how the product behaves, not what is in it.',
  'systemParameters.saved': 'Saved',
  'systemParameters.scoring': 'Scoring',
  'systemParameters.scoringHint': 'Which model a new checklist is born with.',
  'systemParameters.model': 'Scoring model',
  'systemParameters.modelHint':
    'Weight: the numbers are relative and you set each category share. Points: every question carries its own points and a category is worth the sum of them.',
  'systemParameters.target': 'Points target',
  'systemParameters.targetHint':
    'What the question points must add up to under points. Checked before a form is published.',
  'systemParameters.onlyNewTemplates':
    'This affects only templates created from now on. Existing templates carry their own model, and no score already given moves.',
  'systemParameters.showQuestionPoints': 'Show question points to the auditor',
  'systemParameters.showQuestionPointsHint':
    'What each question is worth, beside it on the audit screen. Turn it off and the store cannot work out which questions carry the weight.',
  'systemParameters.showRunningScore': 'Show the running score to the auditor',
  'systemParameters.showRunningScoreHint':
    'The running total and the category figures while a visit is in progress. The audit screen only — a submitted score still appears in lists, on reports and on the map.',
  'systemParameters.displayTakesEffectNow':
    'The two switches above take effect at once, including on visits already in progress. Unlike the scoring model they are not written onto a template, because they decide what is drawn rather than what a number means.',

  'scoringModel.Weight': 'Weight',
  'scoringModel.Points': 'Points',
  'nav.operations': 'Operations',
  'nav.regions': 'Regions',
  'nav.locations': 'Locations',
  'nav.countries': 'Countries',
  'nav.cities': 'Cities',
  'nav.districts': 'Districts',
  'common.code': 'Code',
  'common.name': 'Name',
  'common.description': 'Description',
  'common.status': 'Status',
  'common.active': 'Active',
  'common.inactive': 'Inactive',
  'common.all': 'All',
  'common.locations': 'locations',
  'locationTypes.title': 'Location types',
  'locationTypes.subtitle': 'What decides which checklist is filled in where — mall, high street, outlet.',
  'locationTypes.locations': 'locations',
  'locationTypes.empty': 'No location types yet. At least one is needed before templates can be narrowed by location.',
  'departments.title': 'Departments',
  'departments.subtitle':
    'The units on the chart. You say which of them are head office; the assignee list reads that.',
  'departments.empty': 'No departments yet.',
  'departments.headOffice': 'Head office',
  'departments.headOfficeShort': 'Head office',
  'departments.storeShort': 'Store',
  'operations.title': 'Operations',
  'operations.subtitle': 'The top of the audit chain. Regions hang off it.',
  'operations.regions': 'regions',
  'operations.empty': 'No operations yet.',
  'regions.title': 'Regions',
  'regions.subtitle': 'Belongs to an operation; locations belong to it.',
  'regions.operation': 'Operation',
  'regions.locations': 'locations',
  'regions.empty': 'No regions in this selection.',
  'regions.needOperation': 'Add an operation first — a region has to belong to one.',
  'countries.title': 'Countries',
  'countries.subtitle': 'Which market a template is published for, and what the holiday calendar hangs off.',
  'countries.cities': 'cities',
  'countries.empty': 'No countries yet.',
  'cities.title': 'Cities',
  'cities.subtitle': 'Belongs to a country.',
  'cities.country': 'Country',
  'cities.districts': 'districts',
  'cities.empty': 'No cities in this selection.',
  'cities.needCountry': 'Add a country first.',
  'districts.title': 'Districts',
  'districts.subtitle': 'Belongs to a city.',
  'districts.city': 'City',
  'districts.empty': 'No districts in this selection.',
  'districts.needCity': 'Add a city first.',
  'locations.notANumber': 'Not a number. Use a point or a comma for decimals.',
  'locations.notAPhone': 'Not a phone number. Use digits, spaces, +, ( ) and -.',
  'locations.missing': 'To save, fill in: {fields}',
  'locations.title': 'Locations',
  'locations.subtitle': 'The places that get audited. An audit opens against one of these — without them nothing can start.',
  'locations.newTitle': 'New location',
  'locations.editTitle': 'Edit location',
  'locations.type': 'Location type',
  'locations.region': 'Region',
  'locations.country': 'Country',
  'locations.city': 'City',
  'locations.district': 'District',
  'locations.address': 'Address',
  'locations.phone': 'Phone',
  'locations.coordinates': 'Coordinates',
  'locations.latitude': 'Latitude',
  'locations.longitude': 'Longitude',
  'locations.area': 'Floor area (m2)',
  'locations.openedOn': 'Opened on',
  'locations.isActive': 'Included in planning',
  'locations.isActiveHint': 'A closed location leaves planning and keeps its audit history.',
  'locations.whoAnswers': 'Who answers for it',
  'locations.whoAnswersHint': 'The type decides which checklist applies; the region decides who answers.',
  'locations.whereItStands': 'Where it stands',
  'locations.checkIn': 'Check-in',
  'locations.checkInHint': 'What check-in distance is measured against. Left empty, the system has to take the auditor at their word.',
  'locations.noType': 'no type',
  'locations.noCoordinates': 'no coordinates',
  'locations.auditedCannotDelete': '{count} audits belong to this location — close it instead of deleting it.',
  'locations.empty': 'No locations yet. The audit flow starts here.',
  'error.country.name.required': 'A country needs a name.',
  'error.country.code.length': 'A country code is two or three letters — TR, DE.',
  'error.country.code.letters-only': 'A country code is letters only.',
  'error.city.name.required': 'A city needs a name.',
  'error.city.country.required': 'A city has to belong to a country.',
  'error.district.name.required': 'A district needs a name.',
  'error.district.city.required': 'A district has to belong to a city.',
  'error.operation.name.required': 'An operation needs a name.',
  'error.operation.code.required': 'An operation needs a code.',
  'error.region.name.required': 'A region needs a name.',
  'error.region.operation.required': 'A region has to belong to an operation.',
  'error.location-type.name.required': 'A location type needs a name.',
  'error.department.name.required': 'A department needs a name.',
  'error.location.code.required': 'A location needs a code — it is what the reports show.',
  'error.location.name.required': 'A location needs a name.',
  'error.location.city.without-country': 'Pick a country before a city.',
  'error.location.district.without-city': 'Pick a city before a district.',
  'error.location.city.not-in-country': '{city} is not in {country}.',
  'error.location.district.not-in-city': '{district} is not in {city}.',
  'error.location.coordinate.half': 'Latitude and longitude are entered together. Half a coordinate is a point on the equator, and check-in would measure the distance to it without complaining.',
  'error.location.latitude.range': 'Latitude runs from -90 to 90.',
  'error.location.longitude.range': 'Longitude runs from -180 to 180.',
  'error.location.area.positive': 'A floor area is greater than zero.',
  'error.organisation.country.in-use': "'{name}' is used by {count} records. Move those first.",
  'error.organisation.district.in-use': "'{name}' is used by {count} locations. Move those first.",
  'error.organisation.city.in-use': "'{name}' is used by {count} records. Move those first.",
  'error.organisation.operation.in-use': "'{name}' holds {count} regions. Move those first.",
  'error.organisation.region.in-use': "'{name}' holds {count} locations. Move those first.",
  'error.organisation.location-type.in-use': "'{name}' is used by {count} locations. Change those first.",
  'error.organisation.department.in-use': "'{name}' is in use.",
  'error.organisation.location.in-use': "'{name}' has {count} audits against it. The audits are evidence and they name it — close it rather than deleting it.",
  'error.organisation.not-found': 'That record was not found. The list may be stale — reload the page.',

  // ---------------------------------------------------------------- access
  'nav.users': 'Users',
  'nav.roles': 'Roles',

  'app.people': 'People',
  'nav.groups': 'User groups',
  'groups.title': 'User groups',
  'groups.subtitle': 'Named sets of people that cut across the chart: who a report is filtered by, and who something is granted to together.',
  'groups.add': 'Add group',
  'groups.newTitle': 'New group',
  'groups.editTitle': 'Edit group',
  'groups.members': 'Members',
  'groups.membersHint': 'Who is in this group. Membership keeps no history — unlike a spell in a seat, a group is about today.',
  'groups.andMore': 'and {count} more',
  'groups.grants': 'Roles this group grants',
  'groups.grantsHint': 'Being in this group grants these as well. Roles only, no reach: reach belongs to the seat, or "why does this person see this store" goes back to having two answers.',
  'groups.grantsWarning': 'These roles go to all {count} people in the group at once, and apply at their next sign-in.',
  'groups.noGrants': 'grants nothing',
  'groups.grantsCannotDelete': 'This group grants {count} roles',
  'groups.codeHint': 'A fixed short name: ic-denetim.',
  'groups.isActiveHint': 'A group switched off grants nothing; its members stay.',
  'groups.empty': 'No groups yet',
  'groups.emptyText': 'For filtering a report, or granting something to a set of people at once: internal audit, dealer auditors.',
  'error.user-group.name.required': 'A group needs a name.',
  'error.user-group.member.required': 'A member is somebody.',
  'error.user-group.in-use': 'This group grants roles. Remove them first — deleting it takes them from everybody in the group at once.',


  'nav.professions': 'Professions',
  'nav.chart': 'Positions',
  'professions.title': 'Professions',
  'professions.subtitle': 'What a trade may do is written once; every position for it carries that.',
  'professions.add': 'Add profession',
  'professions.newTitle': 'New profession',
  'professions.editTitle': 'Edit profession',
  'professions.grants': 'Roles granted',
  'professions.grantsHint': 'What everybody of this trade may do, wherever they sit. A position can add to it.',
  'professions.seats': 'Positions',
  'professions.codeHint': 'A fixed short name. It is what reports and records show.',
  'professions.isActiveHint': 'A profession switched off grants nothing. Its positions keep their reach.',
  'professions.seatedCannotDelete': '{count} positions are for this profession',
  'professions.empty': 'No professions yet',
  'professions.emptyText': 'Auditor, store manager, regional manager — the names of the jobs here. Positions are built on them.',
  'chart.title': 'Positions',
  'chart.subtitle': 'The seats on the organisation chart, and who sits in them. Authority belongs to the seat: somebody leaving does not take their region with them, and their successor inherits it.',
  'chart.add': 'Add position',
  'chart.newTitle': 'New position',
  'chart.editTitle': 'Edit position',
  'chart.seat': 'Position',
  'chart.department': 'Department',
  'chart.seatTitle': 'Position title',
  'chart.codeHint': 'A fixed short name: bm-ege, auditor-04.',
  'chart.professionHint': 'Which job this is a seat for. The roles of that trade come with it.',
  'chart.holder': 'Held by',
  'chart.vacant': 'Vacant',
  'chart.vacantCount': '{count} vacant',
  'chart.since': 'since {date}',
  'chart.reach': 'Reach',
  'chart.reachHint': 'Which part of the organisation this seat sees. Whoever sits here sees exactly that.',
  'chart.reachNone': 'Nothing chosen yet. Somebody in a seat with no reach sees no stores at all.',
  'chart.reachGlobalHint': 'Everywhere is selected. A single region cannot be added beside it — remove this first.',
  'chart.everywhere': 'Everywhere',
  'chart.addEverywhere': 'Add everywhere',
  'chart.addOperation': 'Add operation',
  'chart.addRegion': 'Add region',
  'chart.addLocation': 'Add store',
  'chart.removeReach': 'Remove reach over {name}',
  'chart.scope.Global': 'Everywhere',
  'chart.scope.Operation': 'Operation',
  'chart.scope.Region': 'Region',
  'chart.scope.Location': 'Store',
  'chart.grants': 'Roles this position grants',
  'chart.grantsHint': 'On top of what the trade grants. Tick only what is particular to this seat.',
  'chart.reportsTo': 'Reports to',
  'chart.reportsToHint': 'To a seat, not a person — when a manager leaves, everybody under them still reports to the same seat.',
  'chart.top': 'At the top',
  'chart.isActiveHint': 'A position switched off grants neither authority nor reach, but keeps its history.',
  'chart.headcount': 'Authorised headcount',
  'chart.headcountHint': 'Filled against total seats, by department. A vacancy is not a gap in the data; it is a visible need.',
  'chart.total': 'Total',
  'chart.seatSomebody': 'Seat somebody',
  'chart.unseated': 'Without a seat',
  'chart.seatedElsewhere': 'Already seated elsewhere',
  'chart.seatConfirm': 'Seat',
  'chart.from': 'Start date',
  'chart.fromHint': 'Their first day in the seat. It can be in the past: the chart is built after people have already started.',
  'chart.handover': 'Hand over',
  'chart.handoverConfirm': 'Hand over',
  'chart.handoverHint': 'Who takes {title} from {name}? One step: the last day of the one leaving is the day before the first day of the one arriving.',
  'chart.handoverFrom': 'Handover date',
  'chart.handoverFromHint': 'The new holder first day. The previous spell closes the day before, so there is neither a gap nor an overlap.',
  'error.holder.overlaps': 'Another spell in this seat covers that date. A seat is held by one person at a time, in the past as much as today.',
  'error.holder.handover-too-early': 'The current spell began on {from}, so there is nothing to hand over yet. Remove the seating instead — a mistake is not history.',
  'chart.seatHint': 'Who will sit in {title}?',
  'chart.seatExplains': 'They take the authority and reach of this seat the moment they sit down. Nothing else has to be granted; it applies at their next sign-in.',
  'chart.person': 'Person',
  'chart.alreadySeated': 'in {count} seats',
  'chart.vacate': 'End the spell',
  'chart.vacateTitle': 'End the spell of {name} in this seat?',
  'chart.vacateWarning': 'The authority and reach this seat gives them stops. The history stays: the dates they held it remain on record.',
  'chart.heldCannotDelete': 'End the spell first',
  'chart.empty': 'No positions yet',
  'chart.emptyText': 'Each seat is one job: a trade, a department, a part of the organisation. Counting them is your authorised headcount.',
  'error.chart.profession.in-use': 'Positions are for this profession. Move them to another one first.',
  'error.chart.position.in-use': 'This position has a history, or others report to it. Switch it off rather than deleting it.',
  'error.position.already-held': 'Somebody is already in this seat. The chart counts seats, not chairs.',
  'error.position.reports-to.itself': 'A position cannot report to itself.',
  'error.position.reports-to.loop': 'That would put this position under itself, by way of the ones between.',
  'error.position.reach.already-global': 'This position already reaches everywhere. Remove that before naming one part.',
  'error.position.title.required': 'A position needs a title.',
  'error.position.department.required': 'A position sits in a department.',
  'error.position.profession.required': 'A position is a seat for a trade.',
  'error.profession.name.required': 'A profession needs a name.',
  'error.holder.position.required': 'Somebody has to be sitting in something.',
  'error.holder.user.required': 'A seat is held by somebody.',
  'error.holder.ends-before-start': 'A spell in a seat cannot end before it started.',

  'permission.definitions.manage': 'Manage definitions',
  'permission.definitions.manage.hint': 'Visit types, categories, questions, location types, departments.',
  'permission.organisation.manage': 'Manage the organisation',
  'permission.organisation.manage.hint': 'Countries, cities, districts, operations, regions and locations.',
  'permission.templates.manage': 'Manage templates',
  'permission.templates.manage.hint': 'Build and publish the forms audits are scored against.',
  'permission.plans.manage': 'Plan audits',
  'permission.plans.manage.hint': 'Decide who visits which store and when; reschedule or call a visit off.',
  'permission.audits.conduct': 'Conduct audits',
  'permission.audits.conduct.hint': 'Open and fill in an audit in the field.',
  'permission.audits.read': 'Read audits',
  'permission.audits.read.hint': 'See audits and the reports built from them.',
  'permission.actions.respond': 'Respond to actions',
  'permission.actions.respond.hint': 'Answer an action raised against a location.',
  'permission.actions.approve': 'Approve actions',
  'permission.actions.approve.hint': "Approve an answered action — the regional manager's step.",
  'permission.users.manage': 'Manage users',
  'permission.users.manage.hint': 'Create users, decide what roles exist and who holds them. The one that can grant itself.',
  'scope.Country': 'Country',
  'scope.Global': 'Everywhere',
  'scope.Operation': 'Operation',
  'scope.Region': 'Region',
  'scope.Location': 'Location',
  'roles.title': 'Roles',
  'roles.subtitle': 'What the system can do is fixed; what you call the people who do it is yours. Each role is a row.',
  'roles.key': 'Key',
  'roles.keyHint': 'It travels in the token and lands in logs: ASCII letters, digits and hyphens only. Suggested from the name, and tidied as you type.',
  'roles.keyFixed': 'The key never moves — if it did, everyone holding the role would silently lose it.',
  'roles.needsPermission': 'Choose at least one permission to save.',
  'roles.permissions': 'Permissions',
  'roles.permissionsHint': 'Exactly what somebody holding this role may do.',
  'roles.holders': 'Held by',
  'roles.everything': 'everything',
  'roles.builtIn': 'built in',
  'roles.builtInHint': 'The built-in role holds every permission and cannot be edited. An administrator who removes their own right to manage users leaves a system nobody can get back into.',
  'roles.builtInCannotDelete': 'The built-in role cannot be deleted — it is what lets anybody manage anybody.',
  'roles.heldCannotDelete': '{count} people hold this role. Take it off them first.',
  'roles.isActive': 'In use',
  'roles.isActiveHint': 'Switching a role off takes effect at the next sign-in; a session already issued runs to its expiry.',
  'roles.newTitle': 'New role',
  'roles.editTitle': 'Edit role',
  'roles.empty': 'No roles yet.',
  'users.title': 'Users',
  'users.subtitle': 'Who may do what, and where.',
  'users.email': 'Email',
  'users.emailHint': 'What they sign in with. Case does not matter.',
  'users.roles': 'Roles',
  'users.noRoles': 'no roles',
  'users.noRolesHint': 'Holds nothing from a seat, a group or a grant of their own; signing in, they could do nothing.',
  'users.scope.nowhere': 'no reach',
  'users.source.Position': 'From a seat: {through}',
  'users.source.Profession': 'From their trade: {through}',
  'users.source.Group': 'From a group: {through}',
  'users.source.Direct': 'Granted to this person',
  'users.noPassword': 'no password',
  'users.locked': 'locked',
  'users.manageRoles': 'Manage roles',
  'users.setPassword': 'Set password',
  'users.newPassword': 'New password',
  'users.passwordHint': 'At least 12 characters. Length and nothing else — composition rules push people towards passwords a machine guesses faster.',
  'users.initialPassword': 'Initial password',
  'users.initialPasswordHint': 'May be left empty. An account with no password cannot sign in, which is the right state for one created ahead of an invitation.',
  'users.isActive': 'Can sign in',
  'users.isActiveHint': 'A disabled account keeps its history and cannot sign in again.',
  'users.isHeadOffice': 'Head office',
  'users.isHeadOfficeHint':
    'A finding can be handed to this person as head office, whatever department they sit in. This is the route for a department that serves head office and the stores at once. It grants nothing: answering an action still needs that permission.',
  'users.headOfficeShort': 'Head office',
  'users.held': 'Roles held',
  'users.heldHint': 'Everything from their seat, their trade, their groups and anything granted to them. Only the last can be taken away here — a seat\'s role goes when they leave the seat.',
  'users.grant': 'Grant a role',
  'users.grantButton': 'Grant',
  'users.revoke': 'Revoke',
  'users.role': 'Role',
  'users.scope': 'Scope',
  'users.scopeHint': 'This row is the only answer to "who manages the first region". Kept in two places, the answers would drift apart.',
  'users.noScopeTargets': 'Nothing is defined at this scope yet.',
  'users.effective': 'What this adds up to',
  'users.effectiveHint': 'Every active role held, together — seat, trade, group and granted. This is what the next token will carry.',
  'users.newTitle': 'New user',
  'users.editTitle': 'Edit user',
  'users.empty': 'No users yet.',
  'error.role.key.required': 'A role needs a key.',
  'error.role.key.shape': "'{key}' is not a usable key: ASCII letters, digits and hyphens only. It travels in the token and lands in logs, where a Turkish letter is a different character on two machines.",
  'error.role.name.required': 'A role needs a name.',
  'error.role.permissions.empty': 'A role that may do nothing is a role nobody can tell from having none.',
  'error.role.permissions.unknown': "'{permission}' is not something the system can do.",
  'error.role.system.immutable': "'{name}' is built in; its permissions cannot be edited and it cannot be switched off.",
  'error.assignment.user.required': 'An assignment needs a user.',
  'error.assignment.role.required': 'An assignment needs a role.',
  'error.assignment.scope.required': 'A {scope} assignment has to say which one.',
  'error.assignment.scope.global': 'A global assignment reaches everywhere, so it cannot also name one region.',
  'error.user.email.required': 'A user needs an email address.',
  'error.user.email.shape': "'{email}' does not look like an email address.",
  'error.user.name.required': 'A user needs a name.',
  'error.user.password.too-short': 'A password is at least {minimum} characters.',
  'error.access.role.in-use': "'{name}' is held by {count} people. Take it off them first.",
  'error.access.role.system': "'{name}' is built in and cannot be deleted.",
  'error.access.last-administrator': 'This is the last account that can manage users. Give somebody else the administrator role first — nothing in the product can put it back afterwards.',
  'error.access.assignment.duplicate': 'That role is already granted at that scope.',
  'error.access.user.email-taken': 'That email address is already in use.',
  'error.access.user.in-use': "'{name}' has {count} records against them. Audits name who carried them out — close the account rather than deleting it.",
  'users.delete': 'Delete user',
  'users.deleteHint': 'Only an account that has not done anything yet can be deleted.',

  // ---------------------------------------------------------------- parameters
  'nav.checklistTitles': 'Checklist titles',
  'nav.scoreBands': 'Score bands',
  'nav.actionCompanies': 'Action firms',
  'nav.centralTeams': 'Central teams',
  'nav.closedDays': 'Closed days',
  'checklistTitles.title': 'Checklist titles',
  'checklistTitles.subtitle': 'The top of the checklist library. A category can belong to several titles — safety means the same thing on a store audit and on a dealer visit.',
  'checklistTitles.categories': 'Categories',
  'checklistTitles.someCategories': '{count} categories · {questions} questions',
  'checklistTitles.noCategories': 'pick categories',
  'checklistTitles.pickHint': 'Picking this title shows the designer these categories first. Nothing is forbidden — the whole library is still reachable.',
  'checklistTitles.questions': '{count} questions',
  'checklistTitles.noLibrary': 'No categories yet. Add some on the Categories screen first.',
  'checklistTitles.empty': 'No checklist titles yet.',
  'scoreBands.title': 'Score bands',
  'scoreBands.subtitle': 'The letter beside the number. A band states only its lower bound, so two bands cannot claim the same score and none can leave a gap.',
  'scoreBands.code': 'Code',
  'scoreBands.from': 'From',
  'scoreBands.colour': 'Colour',
  'scoreBands.covers': 'Covers',
  'scoreBands.range': '{from} – {until}',
  'scoreBands.andAbove': '{from} and above',
  'scoreBands.empty': 'No score bands yet. Reports cannot put a letter beside the number.',
  'actionCompanies.title': 'Action firms',
  'actionCompanies.subtitle': 'Outside firms an action can be handed to. Not every finding is the store’s to fix.',
  'actionCompanies.addresses': 'Email',
  'actionCompanies.noAddresses': 'no addresses',
  'actionCompanies.empty': 'No action firms yet.',
  'centralTeams.title': 'Central teams',
  'centralTeams.subtitle': 'How reports are sliced at head office. Different from a department: a department owns a finding, a central team slices a report.',
  'centralTeams.empty': 'No central teams yet.',
  'closedDays.title': 'Closed days',
  'closedDays.subtitle': 'The calendar planning reads. A visit booked for a day the shop is shut wastes an auditor’s day, and an audit that never happened is afterwards indistinguishable from one that was skipped.',
  'closedDays.calendar': 'Calendar',
  'closedDays.countryCalendar': 'Country holidays',
  'closedDays.locationCalendar': 'Location closures',
  'closedDays.date': 'Date',
  'closedDays.reason': 'Reason',
  'closedDays.empty': 'Nothing in this calendar.',
  'closedDays.needCountry': 'Add a country first.',
  'closedDays.needLocation': 'Add a location first.',
  'error.checklist-title.name.required': 'A title needs a name.',
  'error.score-band.code.required': 'A band needs a code — it is the letter reports print.',
  'error.score-band.name.required': 'A band needs a name.',
  'error.score-band.from.out-of-scale': 'A band starts somewhere between 0 and 100, which is the scale every form scores on. {from} is not on it.',
  'error.score-band.colour': "'{colour}' is not a colour. Six hex digits, as #1B7F4A.",
  'error.action-company.name.required': 'A firm needs a name.',
  'error.action-company.address': "'{email}' does not look like an email address.",
  'error.central-team.name.required': 'A team needs a name.',
  'error.closed-day.name.required': 'A closed day needs a name — a date with no reason beside it is a date somebody deletes next year without knowing what they are deleting.',
  'error.closed-day.scope.required': 'A {scope} closed day has to say which one.',
  'error.parameters.score-band.duplicate': 'A band already starts at {from}. Two would make the letter depend on which row came back first.',
  'error.parameters.closed-day.duplicate': '{date} is already marked closed here.',
  'error.parameters.checklist-title.in-use': "'{name}' is in use.",
  'error.parameters.score-band.in-use': "'{name}' is in use.",
  'error.parameters.action-company.in-use': "'{name}' is used by {count} actions.",
  'error.parameters.central-team.in-use': "'{name}' is in use.",
  'templates.checklistTitle': 'Checklist title',
  'templates.scope': 'Where it applies',
  'templates.scopeHint': 'Nothing chosen means everywhere. Narrowing is done on purpose — a narrowed template does not appear at a store whose type nobody has filled in.',
  'templates.countries': 'Countries',
  'templates.locationTypes': 'Location types',
  'templates.everyCountry': 'every country',
  'templates.everyLocationType': 'every kind',
  'templates.noCountries': 'No countries defined yet.',
  'templates.noLocationTypes': 'No location types defined yet.',
  'error.organisation.location.closed': "'{code}' is closed. An audit cannot be opened at a closed location.",
  'templates.everywhere': 'everywhere',

  // ---------------------------------------------------------------- answer types
  'answerType.SingleChoice': 'Single choice',
  'answerType.MultipleChoice': 'Multiple choice',
  'answerType.Text': 'Free text',
  'questions.options': 'Options',
  'questions.optionText': 'Option text',
  'questions.optionCount': '{count} options',
  'questions.addOption': '+ Add option',
  'questions.needTwoOptions': 'At least two options — a choice between fewer than two things is not a choice.',
  'questions.optionsSingleHint': 'One option is chosen, and it says what per cent of the question it earns. What the others are worth makes no difference.',
  'questions.optionsMultipleHint': 'Several may be ticked. The question earns the share of the list that was — three of four equal options is 75 per cent.',
  'error.option.text.required': 'An option needs text.',
  'error.option.score.range': 'An option is worth between 0 and 100 per cent of the question. {score} is not.',
  'error.question.options.wrong-type': 'A {type} question has no options to choose from.',
  'error.question.weight.text': 'A free-text question never scores, so it carries no weight.',

  // ---------------------------------------------------------------- the field
  'nav.audits': 'Audits',

  'app.reports': 'Reports',
  'nav.mailSettings': 'Mail recipients',
  'mail.title': 'Mail recipients',
  'mail.subtitle': 'Who the report goes to when a visit is handed in. A list per visit type.',
  'mail.visitType': 'Visit type',
  'mail.visitTypeHint': 'Each visit type has its own list. The number in brackets is how many addresses it holds.',
  'mail.to': 'To',
  'mail.toHint': 'The addresses the report is sent to.',
  'mail.cc': 'Cc',
  'mail.ccHint': 'Copied in. An address already on the To list is dropped from here.',
  'mail.add': 'Add',
  'mail.placeholder': 'name.surname@company.com',
  'mail.invalid': "'{address}' does not look like an e-mail address.",
  'mail.unsaved': 'Not saved',
  'mail.none': 'The list is empty.',
  'mail.saved': 'Saved',
  'mail.sendsNothing': 'Nothing is sent for this visit type.',
  'mail.noVisitTypes': 'No visit types',
  'mail.noVisitTypesText': 'Define at least one under Definitions → Visit types first.',
  // ---------------------------------------------------------------- the map
  'nav.locationMap': 'Map of Türkiye',
  'map.title': 'Map of Türkiye',
  'map.subtitle': 'Every location on one screen. Provinces are coloured by the average of their last visit scores; pointing at a dot shows that location’s figures.',
  'map.refresh': 'Refresh',
  'map.stores': 'Locations',
  'map.visited': 'Visited',
  'map.averageScore': 'Average score',
  'map.openActions': 'Open actions',
  'map.overdueActions': 'Overdue actions',
  'map.legend': 'Score',
  'map.dots': 'Dots',
  'map.dotVisited': 'Visited',
  'map.dotOverdue': 'Has overdue actions',
  'map.dotUnvisited': 'Never visited',
  'map.rowHint': 'Clicking a row opens that store’s visit results.',
  'map.code': 'Code',
  'map.name': 'Store',
  'map.district': 'District',
  'map.locationType': 'Type',
  'map.score': 'Score',
  'map.clickToList': 'click for all of them',
  'map.storeCount': '{count} stores',
  'map.storesOf': 'Stores in {name}',
  'map.unscored': 'Not visited (shaded by store count)',
  'map.zoomIn': 'Zoom in',
  'map.zoomOut': 'Zoom out',
  'map.reset': 'Reset the map',
  'map.howTo': 'Scroll or pinch to zoom, drag to move, click a province. Double-click resets.',
  'map.noStore': 'No location',
  'map.never': 'Never visited',
  'map.firstVisit': 'First visit',
  'map.lastVisit': 'Last visit',
  'map.trend': 'Trend',
  'map.thisYear': 'This year',
  'map.openWithOverdue': '{open} ({overdue} overdue)',
  'map.visitsAndAverage': '{count} visits · avg. {average}',
  'map.noneThisYear': 'No visits this year',
  'map.andMore': '+{count} more',
  'map.clickToSpread': 'Click the province to see them one by one.',
  'map.unplaced': '{count} locations whose city matches no province are not shown:',

  'nav.mapSettings': 'Map figures',
  'mapSettings.title': 'Map figures',
  'mapSettings.subtitle': 'Which figures appear, and in what order, when somebody points at a location on the map.',
  'mapSettings.saved': 'Saved',
  'mapSettings.noneShown': 'Nothing is selected. The location card will show only its code and name.',
  'mapSettings.moveUp': 'Move up',
  'mapSettings.moveDown': 'Move down',
  'mapSettings.kpi.LastVisit': 'Last visit',
  'mapSettings.kpiHint.LastVisit': 'The date, score and letter of the last visit.',
  'mapSettings.kpi.OpenActions': 'Open actions',
  'mapSettings.kpiHint.OpenActions': 'How many actions are open, and how many are past their date.',
  'mapSettings.kpi.Trend': 'Trend',
  'mapSettings.kpiHint.Trend': 'Which way the score moved against the previous visit of the same kind.',
  'mapSettings.kpi.YearToDate': 'This year',
  'mapSettings.kpiHint.YearToDate': 'Visits made this year and their average.',

  'error.map.kpi.order.out-of-range': 'A figure’s order has to be between 0 and 99.',

  // ---------------------------------------------------------------- planning
  'nav.tasks': 'My tasks',
  'tasks.title': 'My tasks',
  'tasks.subtitle': 'Visits planned for you. Soonest deadline first.',
  'tasks.empty': 'Nothing waiting for you',
  'tasks.emptyText': 'When a visit is assigned to you it appears here, ready to start in one click.',
  'tasks.overdue': 'Overdue',
  'tasks.overdueCount': '{count} overdue',
  'tasks.started': 'Started',
  'tasks.form': 'Checklist',
  'tasks.period': 'Period',
  'tasks.plannedBy': 'Planned by',
  'tasks.start': 'Start the visit',
  'tasks.continue': 'Continue',
  'tasks.foot': 'Visits past their period close as missed and appear in the compliance report.',

  'nav.plans': 'Audit plan',
  'plans.title': 'Audit plan',
  'plans.subtitle': 'Who is going where, and by when. Raised in bulk.',
  'plans.new': 'Create plans',
  'plans.choose': 'Choose',
  'plans.form': 'Audit form',
  'plans.noPublishedForm': 'No form template is published, which is why the list is empty. Publish one under Definitions → Audit form templates; a plan can only point at a published form.',
  'plans.formHint': 'Published form templates. A plan cannot point at a draft.',
  'plans.assignedTo': 'Assigned to',
  'plans.from': 'Period from',
  'plans.to': 'Period to',
  'plans.periodHint': 'The visit happens inside this range. An unannounced visit is not pinned to a day.',
  'plans.note': 'Note',
  'plans.noteHint': 'Optional. Goes to the assignee in the mail too.',
  'plans.stores': 'Stores ({count} selected)',
  'plans.search': 'Search code or name',
  'plans.selectShown': 'Select shown',
  'plans.clear': 'Clear',
  'plans.notify': 'Email the assignee',
  'plans.create': 'Create {count} plans',
  'plans.refused': '{count} stores could not be planned:',
  'plans.tally.planned': 'Planned',
  'plans.tally.completed': 'Completed',
  'plans.tally.overdue': 'Overdue',
  'plans.tally.missed': 'Missed',
  'plans.tally.rate': 'Compliance',
  'plans.tally.byAuditor': 'By auditor',
  'plans.tally.from': 'Compliance period — from',
  'plans.tally.to': 'to',
  'plans.tally.periodHint': 'Counts plans whose own period touches this range. Opens on the current month; move it forward to see a period you have just planned.',
  'plans.tally.none': 'No plans in this period.',
  'plans.location': 'Store',
  'plans.period': 'Period',
  'plans.status': 'Status',
  'plans.status.Planned': 'Planned',
  'plans.status.Started': 'Started',
  'plans.status.Completed': 'Completed',
  'plans.status.Missed': 'Missed',
  'plans.status.Cancelled': 'Cancelled',
  'plans.empty': 'No plans',
  'plans.emptyText': 'Use Create plans to assign a checklist to stores and a person.',
  'plans.cancelPlan': 'Cancel the plan',
  'plans.created': '{count} plans created',
  'plans.cancelTitle': 'Cancel the plan: {location}?',
  'plans.cancelConfirm': 'Cancel the plan',
  'plans.onlyPeriod': 'Selected period only',
  'plans.editPlan': 'Edit plan',
  'plans.editTitle': 'Edit plan — {location}',
  'plans.editStarted': 'The visit is under way: its person and start stay. You can move the end later and change the note.',
  'plans.notifyNew': 'E-mail the new person',
  'plans.emptyPeriod': 'No plans in this period',
  'plans.emptyPeriodText': 'There are {count} plans in other periods.',
  'plans.showAllPeriods': 'Show all periods',
  'plans.cancelReason': 'Why is the visit not happening?',
  'plans.truncated': 'More plans matched than are shown. Narrow it with the status filter.',

  'nav.visitResults': 'Visit results',
  'nav.followUp': 'Action follow-up',
  'visits.title': 'Visit results',
  'visits.subtitle': 'Submitted visits, their scores, and how they moved since the last one.',
  'visits.from': 'From',
  'visits.to': 'To',
  'visits.operation': 'Operation',
  'visits.region': 'Region',
  'visits.locationFilter': 'Location',
  'visits.locationType': 'Location type',
  'visits.auditor': 'Auditor',
  'visits.visitType': 'Visit type',
  'visits.below': 'Score below',
  'visits.belowHint': 'Left empty, everything is listed.',
  'visits.all': 'All',
  'visits.excel': 'Excel',
  'error.exportFailed': 'The spreadsheet could not be produced.',
  'visits.apply': 'Filter',
  'visits.reset': 'Reset',
  'visits.count': 'Visits',
  'visits.average': 'Average score',
  'visits.zeroed': 'Zeroed by critical',
  'visits.belowCount': 'Below {score}',
  'visits.location': 'Location',
  'visits.regionalManager': 'Regional manager',
  'visits.date': 'Date',
  'visits.score': 'Score',
  'visits.previous': 'Previous visit',
  'visits.firstVisit': 'First visit',
  'visits.mail': 'Mail',
  'visits.mail.None': '—',
  'visits.mail.Queued': 'Queued',
  'visits.mail.Sent': 'Sent',
  'visits.mail.Failed': 'Failed',
  'visits.trend': 'Trend',
  'visits.trend.Unknown': 'No earlier visit to compare with',
  'visits.trend.Steady': 'Level with the previous visit',
  'visits.trend.Improved': 'Up on the previous visit',
  'visits.trend.Declined': 'Down on the previous visit',
  'visits.empty': 'No results',
  'visits.emptyText': 'No visit was submitted in the range you chose.',
  'visits.truncated': 'Not every matching visit is shown. Narrow the range.',

  'grid.columns': 'Columns',
  'grid.filter': 'Filter',
  'grid.all': 'All',
  'grid.from': 'From',
  'grid.to': 'To',
  'grid.excel': 'Excel',
  'grid.sheetView': 'View',
  'grid.sheetData': 'Data',
  'grid.dropHint': 'Drag a column header here to group by it',
  'grid.removeLevel': 'Remove',
  'grid.grandTotal': 'Grand total',
  'grid.rollUp': 'Roll-up',
  'grid.aggSum': 'Σ Sum',
  'grid.aggAvg': 'x̄ Average',
  'grid.aggCount': '# Count',
  'followUp.title': 'Action follow-up report',
  'followUp.subtitle': 'What happened after the visit: were the actions it raised closed, in time, and how far have the open ones drifted.',
  'followUp.from': 'From',
  'followUp.to': 'To',
  'followUp.dateHint': 'By the day the action was raised. Open and overdue counts are always as of today.',
  'followUp.operation': 'Operation',
  'followUp.region': 'Region',
  'followUp.locationFilter': 'Store',
  'followUp.locationType': 'Store type',
  'followUp.all': 'All',
  'followUp.apply': 'Apply',
  'followUp.reset': 'Reset',
  'followUp.location': 'Store',
  'followUp.status': 'Status',
  'followUp.assignee': 'Responsible',
  'followUp.finding': 'Finding',
  'followUp.category': 'Category',
  'followUp.question': 'Question',
  'followUp.raised': 'Raised',
  'followUp.due': 'Due',
  'followUp.overdueDays': 'Overdue (days)',
  'followUp.daysToClose': 'Days to close',
  'followUp.daysOpen': 'Days open',
  'followUp.regionalManager': 'Regional manager',
  'followUp.open': 'Open',
  'followUp.overdue': 'Overdue',
  'followUp.oldest': 'Oldest overdue',
  'followUp.oldestHint': 'How many days the longest-overdue action has been overdue.',
  'followUp.onTime': 'On time',
  'followUp.avgClose': 'Avg. close',
  'followUp.days': '{n} days',
  'followUp.openTotal': 'Open actions',
  'followUp.overdueTotal': 'Overdue',
  'followUp.onTimeRate': 'On-time closure',
  'followUp.avgCloseDays': 'Avg. days to close',
  'followUp.unresolvedTotal': 'Unresolved',
  'followUp.agingFresh': 'Fresh (≤7 days)',
  'followUp.agingFreshHint': 'A week or less past due — a reminder away from closed.',
  'followUp.agingAging': 'Drifting (8–30 days)',
  'followUp.agingAgingHint': 'Somebody has stopped looking at it.',
  'followUp.agingStale': 'Stale (>30 days)',
  'followUp.agingStaleHint': 'Not slippage any more — a hole in the process.',
  'followUp.empty': 'No actions to follow up',
  'followUp.emptyText': 'No action has been raised in the scope you chose.',
  'nav.actions': 'Actions',
  'raise.open': 'Raise an action',
  'raise.kind': 'Kind of action',
  'raise.kindHint': 'Free closes when the owner says it is done. Approved travels the seats the checklist lays down, and the last one closes it.',
  'raise.title': 'Raise an action',
  'raise.confirm': 'Raise it',
  'raise.target': 'Where it goes',
  'raise.targetHint':
    'A free action can go to head office or to the store; head office is asked two ways, by department or by person. An approved one stays at the store, because the chain above it belongs to that store.',
  'raise.targetDepartments': 'Head-office departments',
  'raise.targetPeople': 'Head-office people',
  'raise.targetStore': 'The store',
  'raise.noHeadOffice': 'no department is marked as head office',
  'raise.noHeadOfficePeople': 'nobody is marked as head office',
  'raise.nobodySeated': 'Nobody is seated here.',
  'raise.seats': 'Seats',
  'raise.people': 'People',
  'raise.problemHint': 'The assignee reads this; they should not have to open the audit to understand it.',
  'raise.problemPlaceholder': 'Boxes on the top shelf are blocking the walkway.',
  'raise.assigneeHint': 'Assigning to the store means the seat, so the action stays put when the manager changes.',
  'raise.dueHint': 'The deadline is yours. The assignee cannot move it, only promise a date against it.',
  'actions.title': 'Actions',
  'actions.subtitle': 'The jobs a visit leaves behind: who has them, by when, and what became of them.',
  'actions.mine': 'Mine',
  'actions.awaitingMe': 'Awaiting me',
  'actions.overdueTab': 'Overdue',
  'actions.all': 'All',
  'actions.finding': 'Finding',
  'actions.problem': 'What was found',
  'actions.where': 'Where',
  'actions.assignee': 'Assignee',
  'actions.due': 'Due',
  'actions.dueOn': 'Due {date}',
  'actions.overdue': 'overdue',
  'actions.raisedBy': 'Raised by {name} · {date}',
  'actions.response': 'Response',
  'actions.responsePlaceholder': 'What was done?',
  'actions.plans': 'Promised dates',
  'actions.newPlan': 'A new promise',
  'actions.newPlanHint': 'The earlier ones stay; this is added beside them. The deadline is the auditor\'s and does not move.',
  'actions.plannedFor': 'Planned for',
  'actions.reason': 'Reason',
  'actions.addPlan': 'Add the promise',
  'actions.needsNewPlan': 'This is past its date. Say when it will be done, and why, before answering.',
  'actions.done': 'Done',
  'actions.cannot': 'Cannot be done',
  'actions.chain': 'Approval chain',
  'actions.yourDecision': 'Your decision',
  'actions.note': 'Note',
  'actions.approve': 'Approve',
  'actions.sendBack': 'Send back',
  'actions.evidence': 'Evidence',
  'actions.evidenceHint': 'A photograph of the work, an invoice, a delivery note. It is what the word “done” means at the next visit.',
  'actions.attach': 'Attach a file',
  'actions.takePhoto': 'Take a photo',
  'actions.uploading': 'Uploading: {name} ({size} MB)…',
  'actions.evidenceNone': 'Nothing attached yet.',
  'actions.evidenceBy': '{name} · {date}',
  'actions.evidenceOpen': 'Open',
  'actions.evidenceGone': 'This file cannot be opened.',
  'actions.sendBackHint': 'A note is required when sending back: the owner has to know what to change.',
  'actions.empty': 'No actions',
  'actions.emptyText': 'An action raised on a finding during a visit appears here.',
  'actions.cannotAnswer': 'cannot answer',
  'actions.kind.Free': 'Free',
  'actions.kind.Approved': 'Approved',
  'actions.status.Open': 'Open',
  'actions.status.InApproval': 'In approval',
  'actions.status.SentBack': 'Sent back',
  'actions.status.Completed': 'Completed',
  'actions.status.Unresolved': 'Not done',
  'actions.decision.Waiting': 'waiting',
  'actions.decision.Approved': 'approved',
  'actions.decision.SentBack': 'sent back',
  'error.action.assignee.required': 'An action needs somebody to do it.',
  'error.action.assignee.cannot-answer': 'That owner cannot answer an action, so this one could never be closed.',
  'error.action.assignee.out-of-reach': 'That owner cannot reach this store, so the action would never appear for them.',
  'error.action.assignee.not-at-store':
    'An approved action stays at the store, because the chain above it belongs to that store. Raise a free action to hand this to head office.',
  'error.action.assignee.not-listed':
    'That owner is neither at this store nor in a head-office department. Seat them somewhere, or mark their department as head office.',
  'error.action.assignee.unchanged': 'This action is already with that owner.',
  'error.action.problem.required': 'What was found has to be written down.',
  'error.action.question.required': 'An action comes from a question.',
  'error.action.audit.required': 'An action comes from an audit.',
  'error.action.raiser.required': 'Somebody raised this action.',
  'error.action.responder.required': 'Somebody answered this.',
  'error.action.response.required': 'The response cannot be left empty.',
  'error.action.plan.reason.required': 'A date with no reason beside it is a date nobody can learn from.',
  'error.action.plan.author.required': 'Somebody promised this date.',
  'error.action.step.approver.required': 'An approval step needs a seat.',
  'error.action.chain.empty': 'An approved action needs at least one approver, or nothing would ever close it.',
  'error.action.chain.unreachable': 'No seat covers this store for one of the approval steps, so the chain cannot be walked.',
  'error.action.chain.ambiguous': 'More than one seat covers this store equally for an approval step, so the approver is not decided.',
  'error.action.send-back.needs-note': 'Sending an action back needs a note saying what is wrong with the answer.',
  'error.action.approver-is-owner': 'The person who answered an action cannot be the one who approves it.',
  'error.action.needs-new-plan': 'This action is past its date. Say when it will be done, and why, before answering.',
  'error.action.not-your-step': 'This action is waiting on a different seat.',
  'error.action.wrong-stage': 'This action has moved on from that step. Refresh the page.',
  'error.action.evidence.key.required': 'Evidence has to say where it is stored.',
  'error.action.evidence.author.required': 'Somebody attached this file.',
  'error.mail.recipient.required': 'A mail needs somebody to go to.',
  'error.mail.subject.required': 'A mail with no subject is not read.',
  'error.mail.body.required': 'A mail needs something in it.',
  'error.mail.recipients.visit-type.required': 'A recipient list belongs to a kind of visit.',
  'error.mail.address.invalid': "'{address}' does not look like an email address.",
  'error.mail.already-sent': 'This mail has already gone.',
  'error.action.evidence.closed': 'This action is finished. What was attached to it is the record of what was shown.',
  'app.field': 'Field',
  'audits.title': 'Audits',
  'audits.subtitle': 'The visits you carried out.',
  'audits.start': '+ Start an audit',
  'audits.location': 'Location',
  'audits.form': 'Form',
  'audits.when': 'Date',
  'audits.report': 'Report (PDF)',
  'error.reportFailed': 'The report could not be produced.',
  'audits.score': 'Score',
  'audits.questions': 'questions',
  'audits.empty': 'No audits yet.',
  'audits.noLocations': 'No locations defined. An audit opens against one.',
  'audits.whichForm': 'Which checklist',
  'audits.whichFormHint': 'What applies at this store today. Not everything — country, location type and date narrow it.',
  'audits.noneApply': 'Nothing applies at this store today. Its location type may be missing.',
  'audits.criticalFailed': 'critical',
  'auditStatus.Draft': 'In progress',
  'auditStatus.Submitted': 'Submitted',
  'auditStatus.Appealed': 'Appealed',
  'auditStatus.Closed': 'Closed',
  'audit.notFound': 'Audit not found.',
  'audit.saving': 'saving',
  'audit.saveFailed': 'not saved',
  'audit.submittedOn': 'Submitted {when} · score {score}',
  'audit.notes': 'Visit note',
  'audit.blocking': '{count} things stand in the way',
  'audit.submit': 'Submit the audit',
  'audit.submitWarning': 'Once submitted the answers cannot be changed and the report goes out. There is no way back.',
  'audit.submitScore': 'Calculated score: {score}',
  'audit.submitConfirm': 'Submit',
  'audit.backToList': 'Back to the list',
  'audit.yes': 'Yes',
  'audit.no': 'No',
  'audit.skip': 'Not applicable',
  'audit.unskip': 'Undo',
  'audit.skipped': 'not applicable',
  'audit.exempt': 'exempt',
  'audit.pointsLeft': '{points} points left the form',
  'audit.pointsSpread': '{points} points went to the rest of the category',
  'audit.pointsSpreadForm': 'Category not applicable; its points went to the other categories',
  'audit.commentWritten': 'Comment written',
  'audit.addEvidence': 'Photo / Video',
  'audit.takePhoto': 'Take a photo',
  'audit.takeVideo': 'Record a video',
  'audit.fromDevice': 'Upload from device',
  'audit.videoFromGallery': 'Video from gallery',
  'audit.startRecording': 'Start recording',
  'audit.stopRecording': 'Stop recording',
  'audit.camera.denied':
    'The camera was not allowed. You can grant it from the permissions in the address bar, or upload from the device instead.',
  'audit.camera.none': 'No usable camera was found on this device.',
  'audit.camera.failed': 'The camera could not be opened. You can upload from the device instead.',
  'audit.mediaPending': 'This file has not been sent yet.',
  'audit.mediaExpired': 'The file could not be opened; its link may have expired.',
  'audit.videoAlt': '{code} · video {n}',
  'audit.commentNeededShort': 'Comment required',
  'audit.running': 'Running {score}',
  'audit.runningHint':
    'What the answers given so far come to. Not the result until the visit is finished — the remaining questions will move it.',
  'audit.worth': '{weight} points',
  'audit.worthReduced': '{declared} → {effective} points',
  'audit.categoryRuledOut': '{declared} points → not applicable',
  'audit.addPhoto': 'Photo',
  'audit.photoCount': '{count} photos',
  'audit.photoNeeded': 'A photo is required',
  'audit.photoUnavailable': 'cannot open',
  'audit.comment': 'Comment',
  'audit.commentNeeded': 'required',
  'audit.guidance': 'Guidance',
  'audit.textPlaceholder': 'Write what you saw',
  'audit.unknownType': 'this question type could not be read',
  'error.audit.answer.shape': 'That is not how this question is answered.',
  'error.audit.incomplete': 'This audit is not ready to be submitted.',
  'error.audit.not-submitted': "A draft's report is not kept; it has to be submitted first.",
  'error.report.key.required': 'A stored report has to say where it is.',
  'error.audit.not-draft': 'A submitted audit cannot be changed.',
  'error.audit.template.does-not-apply': "'{template}' does not apply at {location} today.",
  'error.audit.photo.empty': 'The file is empty.',
  'error.audit.photo.too-large': 'This may be up to {limit} MB.',
  'error.audit.photo.type': "'{type}' is not a kind of photo or video this accepts. On an iPhone: Settings → Camera → Formats → Most Compatible. On a Samsung: turn off “High efficiency pictures” in the camera settings.",
  'audit.waiting': '{count} waiting',
  'audit.unanswered': '{count} questions unanswered',
  'audit.submitQueued': 'Submission queued — it goes up when there is a signal.',
  'audit.rejected': '{count} operations the server would not accept',
  'audit.rejectedElsewhere': 'On another audit ({audit}) — {reason}',
  'audit.fileNotAdded': 'Not added',
  'audit.fileNotRead': 'The phone could not read the file; choose it again.',
  'audit.fileNotStored': 'The file could not be kept on the device; choose it again.',
  'audit.stuck': '{count} files could not be sent',
  'audit.stuckHint': 'They are on screen but have not reached the server. They will go on their own once the connection recovers; before closing this visit, make sure this is clear.',
  'audit.onlyMissing': '{count} left',
  'audit.notAnswered': 'Not answered',
  'audit.answered': 'Answered',
  'audit.owesComment': 'Needs a comment',
  'audit.owesPhoto': 'Needs a photo',
  'audit.owesBoth': 'Needs a comment and a photo',
  'templates.signature': 'Signature',
  'templates.signatureNo': 'No signature',
  'templates.signatureYes': 'Signed for at the store',
  'audit.signature': 'Signature',
  'audit.sign': 'Sign',
  'audit.signClear': 'Clear',
  'audit.signAgain': 'Take it again',
  'audit.signNeeded': 'signature needed',
  'audit.signedBy': 'Signed by',
  'audit.signedByHint': 'Full name. Nobody can read the drawing; this is the part that is readable.',
  'error.audit.signature.image': 'A signature needs a drawing.',
  'error.audit.signature.name': 'A signature needs the name of whoever signed. A drawing with nothing beside it names nobody.',

  // ---------------------------------------------------------------- shell, renamed
  "nav.mainMenu": "Main menu",
  "nav.collapse": "Collapse menu",
  "nav.expand": "Expand menu",
  "nav.openMenu": "Open menu",
  "nav.closeMenu": "Close menu",
  "nav.skipToContent": "Skip to content",
  "theme.label": "Appearance",
  "theme.system": "Match device",
  "theme.light": "Light",
  "theme.dark": "Dark",

  // ---------------------------------------------------------------- table furniture
  "common.actions": "Actions",
  "common.moreActions": "More actions",
  "common.search": "Search",
  "common.saving": "Saving…",
  "common.copy": "Duplicate",
  "common.clearFilters": "Clear filters",
  "common.pageSize": "Per page",
  "common.pagination": "Pagination",
  "common.previousPage": "Previous page",
  "common.nextPage": "Next page",
  "common.total.one": "{count} record in total",
  "common.total.other": "{count} records in total",
  "common.shown.one": "{count} record",
  "common.shown.other": "{count} records",
  "common.noResults": "Nothing matched “{term}”",
  "common.noResultsText": "Try a shorter search, or clear the filters.",
  "common.loadFailed": "The list could not be loaded",
  "common.loadFailedText": "The connection may have dropped. Trying again is usually enough.",
  "common.deleteTitle": "Delete {name}?",
  "common.deleteWarning": "This cannot be undone.",
  "common.inUse.one": "Used in {count} record.",
  "common.inUse.other": "Used in {count} records.",

  // ---------------------------------------------------------------- login
  "login.lead": "Enter your account details to continue.",
  "login.required": "Both the email address and the password are needed.",
  "login.showPassword": "Show password",
  "login.hidePassword": "Hide password",
  "login.help": "If you cannot get into your account, contact your system administrator.",
  "login.brand.headline": "Every audit,",
  "login.brand.headlineAccent": "pointing the same way.",
  "login.brand.text": "Plan store audits in one place, fill them in offline in the field, and see the score straight away.",
  "login.brand.foot": "© {year} Norva · Sumo",

  // ---------------------------------------------------------------- behaviour badges
  "badgeCritical.Category": "Critical · category",
  "badgeCritical.Form": "Critical · audit",
  "badgeCriticalHint.Category": "A negative answer zeroes this category.",
  "badgeCriticalHint.Form": "A negative answer zeroes the whole audit.",
  "badgeNa.Category": "N/A · category",
  "badgeNa.Form": "N/A · form",
  "badgeNaHint.Category": "Marked not applicable, its weight is shared out within its category.",
  "badgeNaHint.Form": "Marked not applicable, its weight leaves the form altogether.",
  "badgeComment.WhenNegative": "Comment · when negative",
  "badgeComment.Always": "Comment · always",
  "badgeCommentHint.WhenNegative": "A negative answer owes a comment.",
  "badgeCommentHint.Always": "Every answer owes a comment.",
  "badgePhoto.WhenNegative": "Photo · when negative",
  "badgePhoto.Always": "Photo · always",
  "badgePhotoHint.WhenNegative": "A negative answer owes a photograph.",
  "badgePhotoHint.Always": "Every answer owes a photograph.",

  // ---------------------------------------------------------------- photographs
  "image.loading": "Loading image",
  "image.error": "The image could not be loaded. Tap to try again.",
  "image.open": "Enlarge",

  // ---------------------------------------------------------------- connectivity
  "sync.offline": "Offline — changes are being kept on this device",
  "sync.pending.one": "{count} change waiting to be sent",
  "sync.pending.other": "{count} changes waiting to be sent",
  "sync.failed": "Sync failed",
  "sync.synced": "Synced",
  "sync.offlineAction": "This cannot be done offline.",
  "visitTypes.searchPlaceholder": "Search visit types…",
  "visitTypes.emptyText": "A visit type says why an audit is being done. Each template picks one.",
  "categories.searchPlaceholder": "Search by category name or code…",
  "categories.emptyText": "Categories group the questions, and scoring is worked out per category.",
  "categories.inUseWarning.one": "This category holds {count} question, which would be left out of reach.",
  "categories.inUseWarning.other": "This category holds {count} questions, which would be left out of reach.",
  "questions.add": "Add question",
  "questions.emptyText": "Questions are what audit templates are built from. A template picks from this library.",
  "questions.optionScore": "Score",
  "questions.inUseWarning.one": "This question is used in {count} template.",
  "questions.inUseWarning.other": "This question is used in {count} templates.",
  "templates.add": "Add template",
  "templates.newTitle": "New template",
  "templates.createAndOpen": "Create and open",
  "templates.emptyText": "A template brings together the visit type, the questions and the scoring. Audits are carried out against one.",
  "templates.outOfPeriodHint": "Published, but outside its effective dates today.",
  'templates.openToPublish': 'Open to publish…',
  'templates.draftHint': 'Not in use yet. Open the template to publish it — anything missing is listed there.',
  "templates.deleteQuestions.one": "This template holds {count} question.",
  "templates.deleteQuestions.other": "This template holds {count} questions.",
  "audits.emptyText": "Pick a location and start with a checklist that applies to it. Audits can be filled in offline in the field.",
  "users.add": "Add user",
  "users.search": "Search by name or email…",
  "users.emptyText": "Users sign in; what they can do depends on the roles you give them.",
  "users.deleteWarning": "The user and every role they hold are removed. The audits they carried out stay.",
  "roles.add": "Add role",
  "roles.emptyText": "A role is your own word for a job; permissions are what the product can do. This is where the two meet.",
  "locations.add": "Add location",
  "locations.search": "Search by code or name…",
  "locations.emptyText": "An audit happens at a location. Its type decides which checklist applies.",
  "audit.photoAlt": "{code} photograph {n}",
  "templates.noQuestionsText": "Pick questions from the library on the right. The weights can be set afterwards.",
  "login.unreachable": "The server could not be reached. Nothing is wrong with your password — check the connection and try again.",
  "locationTypes.inUseWarning.one": "There is {count} location of this type.",
  "locationTypes.inUseWarning.other": "There are {count} locations of this type.",
  "operations.inUseWarning.one": "This operation holds {count} region.",
  "operations.inUseWarning.other": "This operation holds {count} regions.",
  "regions.inUseWarning.one": "This region holds {count} location.",
  "regions.inUseWarning.other": "This region holds {count} locations.",
  "countries.inUseWarning.one": "This country holds {count} location.",
  "countries.inUseWarning.other": "This country holds {count} locations.",
  "countries.hasCitiesWarning.one": "This country holds {count} city.",
  "countries.hasCitiesWarning.other": "This country holds {count} cities.",
  "cities.inUseWarning.one": "This city holds {count} location.",
  "cities.inUseWarning.other": "This city holds {count} locations.",
  "cities.hasDistrictsWarning.one": "This city holds {count} district.",
  "cities.hasDistrictsWarning.other": "This city holds {count} districts.",
  "districts.inUseWarning.one": "This district holds {count} location.",
  "districts.inUseWarning.other": "This district holds {count} locations.",
  "checklistTitles.inUseWarning.one": "This title groups {count} category.",
  "checklistTitles.inUseWarning.other": "This title groups {count} categories.",
  "scoreBands.deleteWarning": "Removing a band leaves the scores under it without one; check what is left.",
  "audits.discard": "Discard draft",
  "audits.discardTitle": "Discard the draft at {location}?",
  "audits.discardWarning": "The answers and photographs go with it. The audit trail keeps a record.",
  "error.audit.not-yours": "Another auditor opened this visit; only they can discard it.",
  'error.audit.has-actions': 'Actions have been raised from this visit ({count}), so it cannot be discarded. They are tracked by their own numbers and would be left pointing at nothing.',
};
